import { useEffect, useRef, useState } from 'react';

/**
 * Прятать прилипшую шапку на прокрутке вниз и возвращать на прокрутке вверх.
 *
 * ## Зачем
 *
 * Каталог по QR (`/showroom`) идёт без шапки сайта: на нём своя шапка салона,
 * и она уезжала вместе с карточками насовсем — вернуться к языку или на
 * главную можно было, только домотав до самого верха (#1456). Шапка сайта
 * на главной уже ведёт себя так: уходит, пока листают, и приходит, когда
 * тянут назад.
 *
 * ## Почему sticky + сдвиг, а не fixed
 *
 * Шапка остаётся `position: sticky` и только уезжает за верх окна сдвигом.
 * Переключение в `fixed` вынимало бы её из потока, и сетка под ней прыгала
 * бы на высоту шапки в момент появления.
 *
 * Порог в высоту самой шапки: в самом верху страницы она не прячется, иначе
 * первый же пиксель прокрутки вниз уводил бы её с места.
 *
 * Направление меряется через rAF: `scroll` приходит чаще кадра.
 *
 * @param {boolean} [enabled=true] — false: не следить, шапка всегда на месте.
 * @returns {[React.RefObject, boolean]} ref для шапки и признак «спрятана».
 */
export function useHideOnScrollDown(enabled = true) {
  const ref = useRef(null);
  const [hidden, setHidden] = useState(false);

  useEffect(() => {
    if (!enabled) {
      setHidden(false);
      return undefined;
    }
    let last = window.scrollY;
    let ticking = false;

    const measure = () => {
      ticking = false;
      const y = window.scrollY;
      const height = ref.current?.offsetHeight ?? 0;
      if (y !== last) setHidden(y > last && y > height);
      last = y;
    };

    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(measure);
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [enabled]);

  return [ref, hidden];
}
